import { useCallback } from 'react';
import { defineMessages, type MessageValue, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@garnish/overlays';

import {
  type BundleMachineTypes,
  type BundleSelectProduct,
  type useBundleContent,
} from '@order/components-menu';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Shows one notice for the whole bundle once every product has settled. Copy
 * from Contentful wins over the bundled copy.
 */
export const useBundleAddedToBagNotice = (params: Params) => {
  const { productsBySlug, successBanner, partialSuccessBanner } = params;

  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();

  return useCallback(
    (summary: AddToBagSummary) => {
      const { addedCount, failedProductSlugs } = summary;

      const failedCount = failedProductSlugs.length;
      const hasFailedProducts = failedCount > 0;
      const values = {
        added_number: addedCount,
        total_number: addedCount + failedCount,
        failed_product_names: getProductNames({
          productSlugs: failedProductSlugs,
          productsBySlug,
        }),
      };
      const successText = successBanner
        ? fillPlaceholders({ template: successBanner, values })
        : formatMessage(messages.allAdded, values);
      const partialSuccessText = partialSuccessBanner
        ? fillPlaceholders({ template: partialSuccessBanner, values })
        : formatMessage(messages.someAdded, values);
      const text = hasFailedProducts ? partialSuccessText : successText;

      addNoticeBanner({
        palette: hasFailedProducts ? 'neutral' : 'success',
        text,
        autoHideTimeout: hasFailedProducts
          ? PARTIAL_NOTICE_AUTO_HIDE_TIMEOUT
          : SUCCESS_NOTICE_AUTO_HIDE_TIMEOUT,
      });
    },
    [addNoticeBanner, formatMessage, partialSuccessBanner, productsBySlug, successBanner],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Names each product once, even when it was picked more than once.
 */
function getProductNames(params: GetProductNamesParams) {
  const { productSlugs, productsBySlug } = params;

  const uniqueProductSlugs = [...new Set(productSlugs)];

  return uniqueProductSlugs
    .map((productSlug) => productsBySlug.get(productSlug)?.name)
    .filter(Boolean)
    .join(', ');
}

function fillPlaceholders(params: FillPlaceholdersParams) {
  const { template, values } = params;

  return template
    .replaceAll('{added_number}', String(values.added_number))
    .replaceAll('{total_number}', String(values.total_number))
    .replaceAll('{failed_product_names}', values.failed_product_names);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SUCCESS_NOTICE_AUTO_HIDE_TIMEOUT = 3000;
const PARTIAL_NOTICE_AUTO_HIDE_TIMEOUT = 4000;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly allAdded: { readonly added_number: MessageValue };
  readonly someAdded: {
    readonly added_number: MessageValue;
    readonly failed_product_names: MessageValue;
    readonly total_number: MessageValue;
  };
}>({
  allAdded: {
    defaultMessage: '{added_number} products added to your bag.',
    description: 'Bundle | Added to bag notice | Every product added',
  },
  someAdded: {
    defaultMessage:
      '{added_number} of {total_number} products added. We couldn’t add {failed_product_names}.',
    description: 'Bundle | Added to bag notice | Some products added',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = Pick<BundleContent, 'successBanner' | 'partialSuccessBanner'> & {
  productsBySlug: Map<string, BundleSelectProduct>;
};

type GetProductNamesParams = {
  productSlugs: string[];
  productsBySlug: Map<string, BundleSelectProduct>;
};

type FillPlaceholdersParams = {
  template: string;
  values: NoticeValues;
};

type NoticeValues = {
  added_number: number;
  total_number: number;
  failed_product_names: string;
};

type BundleContent = ReturnType<typeof useBundleContent>['content'];

type AddToBagSummary = BundleMachineTypes.AddToBagSummary;
